import theme from '@css/theme';
import { Button, ButtonProps } from 'antd';
import styled, { css } from 'styled-components';

const defaultTypeColors = {
  default: {
    border: theme.token.colorBorder,
    color: theme.token.colorText,
    hoverBackground: theme.token.colorBgTextHover,
    activeBackground: theme.token.colorBgTextActive,
    outlineColor: theme.token.colorBorder,
  },
  danger: {
    border: theme.token.colorErrorBorder,
    color: theme.token.colorError,
    hoverBackground: theme.token.colorErrorBg,
    activeBackground: theme.token.colorErrorBgHover,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    border: theme.token.colorSuccessBorder,
    color: theme.token.colorSuccess,
    hoverBackground: theme.token.colorSuccessBg,
    activeBackground: theme.token.colorSuccessBgHover,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export type Variant = keyof typeof defaultTypeColors;

const commonButtonStyles = css`
  font-weight: 600;

  &:disabled {
    /* prevent layout shift with an invisible border */
    border-color: rgba(0, 0, 0, 0);
  }

  &:not(:disabled):focus {
    outline-offset: 2px !important;
    outline-width: 2px !important;
  }

  ${({ size }: { size?: ButtonProps['size'] }) =>
    size === 'small' ? `padding: 0 ${theme.token.paddingXS}px !important;` : ''}
`;

export const DefaultButton = styled(Button)<{ variant: Variant }>`
  ${commonButtonStyles}

  border-color: ${({ variant }) => defaultTypeColors[variant].border};
  color: ${({ variant }) => defaultTypeColors[variant].color};

  &:hover:enabled {
    border-color: ${({ variant }) => defaultTypeColors[variant].border} !important;
    color: ${({ variant }) => defaultTypeColors[variant].color} !important;
    background: ${({ variant }) => defaultTypeColors[variant].hoverBackground} !important;
  }

  &:active:enabled {
    border-color: ${({ variant }) => defaultTypeColors[variant].border} !important;
    color: ${({ variant }) => defaultTypeColors[variant].color} !important;
    background: ${({ variant }) => defaultTypeColors[variant].activeBackground} !important;
  }

  &focus:enabled {
    outline-color: ${({ variant }) => defaultTypeColors[variant].outlineColor} !important;
  }
`;

const primaryButtonColors = {
  default: {
    background: theme.token.colorPrimaryBase,
    hoverBackground: theme.token.colorPrimaryHover,
    activeBackground: theme.token.colorPrimaryActive,
    outlineColor: theme.token.colorPrimaryBorder,
  },
  danger: {
    background: theme.token.colorErrorBase,
    hoverBackground: theme.token.colorErrorHover,
    activeBackground: theme.token.colorErrorActive,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    background: theme.token.colorSuccessVariant,
    hoverBackground: theme.token.colorSuccessHover,
    activeBackground: theme.token.colorSuccessActive,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export const PrimaryButton = styled(Button)<{ variant: Variant }>`
  ${commonButtonStyles}

  background: ${({ variant }) => primaryButtonColors[variant].background};

  &:hover:enabled {
    background: ${({ variant }) => primaryButtonColors[variant].hoverBackground} !important;
  }

  &:active:enabled {
    background: ${({ variant }) => primaryButtonColors[variant].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant }) => primaryButtonColors[variant].outlineColor} !important;
  }
`;

const secondaryTypeColors = {
  default: {
    color: theme.token.colorPrimaryText,
    background: theme.token.colorPrimarySubdued,
    hoverBackground: theme.token.colorPrimarySubduedHover,
    activeBackground: theme.token.colorPrimarySubduedActive,
    outlineColor: theme.token.colorPrimaryBorder,
  },
  danger: {
    color: theme.token.colorErrorText,
    background: theme.token.colorErrorSubdued,
    hoverBackground: theme.token.colorErrorSubduedHover,
    activeBackground: theme.token.colorErrorSubduedActive,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    color: theme.token.colorSuccessText,
    background: theme.token.colorSuccessSubdued,
    hoverBackground: theme.token.colorSuccessSubduedHover,
    activeBackground: theme.token.colorSuccessSubduedActive,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export const SecondaryButton = styled(Button)<{ variant: Variant }>`
  ${commonButtonStyles}

  color: ${({ variant }) => secondaryTypeColors[variant].color};

  /* prevent layout shift with an invisible border */
  border-color: rgba(0, 0, 0, 0) !important;

  background: ${({ variant }) => secondaryTypeColors[variant].background};

  &:hover:enabled {
    background: ${({ variant }) => secondaryTypeColors[variant].hoverBackground} !important;
    color: ${({ variant }) => secondaryTypeColors[variant].color} !important;
  }

  &:active:enabled {
    color: ${({ variant }) => secondaryTypeColors[variant].color} !important;
    background: ${({ variant }) => secondaryTypeColors[variant].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant }) => secondaryTypeColors[variant].outlineColor} !important;
  }
`;

export const LinkButton = styled(Button)<{ $noPadding?: boolean }>`
  ${({ $noPadding }) => $noPadding && 'padding-inline: 0 !important;'}

  font-weight: 600;
  > *:hover:enabled,
  > *:focus:enabled {
    cursor: pointer;
    text-decoration: underline;
    outline: none;
  }

  &focus-visible:enabled {
    outline: 0;
    outline-offset: 0;
  }
`;

export const IconFix = styled.span`
  :not(:last-child) {
    margin-inline-end: ${theme.token.marginXS}px;
  }
`;
